import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, User } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../utils/apiClient';
import { NEXT_STEP_URL } from '../../../routing/nextStep';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error';
}

interface FilterOption {
  label: string;
  value: string;
}

const MIN_INTERESTS = 3;
const MAX_INTERESTS = 10;
const INITIAL_VISIBLE = 12;
const LOAD_MORE_COUNT = 12;

const normalize = (str: string) =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const InterestsPage: React.FC = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [domains, setDomains] = useState<FilterOption[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    // Si le user est déjà passé par cette étape, on le renvoie à l'app.
    // (Le backend nous l'aura signalé via nextStep === 'app'.)
    if (user?.nextStep === 'app' || user?.hasOnboardingInterests) {
      navigate(NEXT_STEP_URL.app, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await apiClient('/jurisprudence-metadata/filters/legal-domains-ranked');

        if (!response.ok) throw new Error('Erreur lors du chargement');

        const data = await response.json();
        if (data.success && data.data?.legalDomains) {
          setDomains(data.data.legalDomains);
        }
      } catch (error) {
        console.error('Erreur:', error);
        addNotification('Erreur lors du chargement des domaines.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchDomains();
  }, []);

  const addNotification = (message: string, type: 'success' | 'error') => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const filteredDomains = useMemo(() => {
    if (!searchQuery.trim()) return domains;
    const normalizedQuery = normalize(searchQuery);
    return domains.filter((d) => normalize(d.label).includes(normalizedQuery));
  }, [domains, searchQuery]);

  const displayedDomains = useMemo(() => {
    if (searchQuery.trim()) return filteredDomains;
    return filteredDomains.slice(0, visibleCount);
  }, [filteredDomains, visibleCount, searchQuery]);

  const hasMore = !searchQuery.trim() && visibleCount < filteredDomains.length;

  const toggleDomain = (value: string) => {
    setSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      if (prev.length >= MAX_INTERESTS) return prev;
      return [...prev, value];
    });
  };

  const handleConfirm = async () => {
    if (selected.length < MIN_INTERESTS) {
      addNotification(`Sélectionnez au moins ${MIN_INTERESTS} centres d'intérêt.`, 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient('/users/onboarding-interests', {
        method: 'POST',
        body: JSON.stringify({ onboardingInterests: selected }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Erreur lors de la sauvegarde');
      }

      setUser((prev: User | null) => ({
        ...(prev as User),
        hasOnboardingInterests: true,
        nextStep: 'app',
      }));

      navigate(NEXT_STEP_URL.app, { replace: true });
    } catch (error) {
      console.error('Erreur:', error);
      addNotification(
        error instanceof Error ? error.message : 'Erreur lors de la sauvegarde.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-mcdm-primary-100 dark:bg-mcdm-third-500 px-4 relative">
      {/* Notifications */}
      <div className="fixed top-4 right-4 z-50">
        <AnimatePresence initial={false}>
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ duration: 0.5 }}
              className={`mb-4 p-4 rounded shadow-lg text-sm text-white ${
                notification.type === 'success'
                  ? 'bg-mcdm-secondary-400 dark:bg-mcdm-secondary-500'
                  : 'bg-red-500 dark:bg-red-600'
              }`}
            >
              <div className="flex justify-between items-center">
                <div>{notification.message}</div>
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="ml-4 focus:outline-none"
                >
                  &times;
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="bg-white dark:bg-mcdm-third-400 shadow-sm rounded-lg px-8 md:px-12 pt-8 pb-10 max-w-2xl w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
            Vos centres d'intérêt
          </h2>
          <p className="mt-2 text-mcdm-third-500 dark:text-mcdm-primary-200 text-sm">
            Sélectionnez entre {MIN_INTERESTS} et {MAX_INTERESTS} domaines juridiques qui vous intéressent.
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-5">
          <input
            type="text"
            placeholder="Rechercher un domaine..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setVisibleCount(INITIAL_VISIBLE);
            }}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-mcdm-third-300 rounded-lg
              text-mcdm-third-500 dark:text-mcdm-primary-100
              bg-white dark:bg-mcdm-third-300
              placeholder:text-gray-400 dark:placeholder:text-gray-500
              focus:outline-none focus:ring-2 focus:ring-mcdm-secondary-300 dark:focus:ring-mcdm-secondary-400
              text-sm"
          />
        </div>

        {/* Compteur */}
        <div className="text-center mb-4">
          <p className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-300">
            {selected.length}/{MAX_INTERESTS} sélectionnés
            {selected.length < MIN_INTERESTS && (
              <span className="ml-1 text-mcdm-third-300 dark:text-mcdm-primary-400">
                (minimum {MIN_INTERESTS})
              </span>
            )}
          </p>
          {filteredDomains.length === 0 && !loading && (
            <p className="text-sm text-gray-400 mt-1">Aucun résultat</p>
          )}
        </div>

        {/* Grille de chips */}
        {loading ? (
          <div className="flex justify-center py-12">
            <svg
              className="animate-spin h-8 w-8 text-mcdm-secondary-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap justify-center gap-2.5 mb-5">
              {displayedDomains.map((domain) => {
                const isSelected = selected.includes(domain.value);
                const isDisabled = !isSelected && selected.length >= MAX_INTERESTS;

                return (
                  <button
                    key={domain.value}
                    onClick={() => !isDisabled && toggleDomain(domain.value)}
                    disabled={isDisabled}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition-all duration-200 border
                      ${
                        isSelected
                          ? 'bg-mcdm-green-400 dark:bg-mcdm-green-500 text-white border-mcdm-green-400 dark:border-mcdm-green-500'
                          : isDisabled
                          ? 'bg-gray-100 dark:bg-mcdm-third-300 text-gray-300 dark:text-gray-500 border-gray-200 dark:border-mcdm-third-300 cursor-not-allowed'
                          : 'bg-mcdm-primary-100 dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-200 border-gray-200 dark:border-mcdm-third-200 hover:border-mcdm-secondary-300 dark:hover:border-mcdm-secondary-400 hover:text-mcdm-secondary-500 dark:hover:text-mcdm-secondary-300 cursor-pointer'
                      }`}
                  >
                    {domain.label}
                  </button>
                );
              })}
            </div>

            {/* Bouton Voir plus */}
            {hasMore && (
              <div className="text-center mb-5">
                <button
                  onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_COUNT)}
                  className="text-sm text-mcdm-secondary-400 dark:text-mcdm-secondary-300 hover:text-mcdm-secondary-500 dark:hover:text-mcdm-secondary-200 focus:outline-none"
                >
                  Voir plus ({filteredDomains.length - visibleCount} restants)
                </button>
              </div>
            )}
          </>
        )}

        {/* Bouton Confirmer */}
        <div className="pt-4 border-t border-gray-100 dark:border-mcdm-third-300">
          <button
            onClick={handleConfirm}
            disabled={selected.length < MIN_INTERESTS || saving}
            className={`w-full py-2.5 px-4 rounded-lg text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-mcdm-secondary-300 dark:focus:ring-mcdm-secondary-500
              ${
                selected.length < MIN_INTERESTS || saving
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-mcdm-green-400 dark:bg-mcdm-green-400 hover:bg-mcdm-green-500 dark:hover:bg-mcdm-green-500'
              }`}
          >
            {saving ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Enregistrement...
              </>
            ) : (
              'Confirmer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterestsPage;
